import React from "react";
import ReactDOM from "react-dom/client";
// DOM guard first: it must wrap removeChild/insertBefore before React mounts.
import "@/lib/domGuard";
import "@/index.css";
// Account colour themes (Personalización › Apariencia): overrides that only
// apply under html[data-zb-theme], so the classic look is untouched.
import "@/uiTheme.css";
import { i18nReady, preloadAllLanguages } from "@/lib/i18n";
import { installApiAuth } from "@/lib/apiAuth";
import App from "@/App";
import { preloadPublicRoute } from "@/publicRoutes";
import { installClickTracking } from "@/lib/analytics";
import { findPage, pageLanguageAt } from "@/lib/seo";
import { ensureSiteLanguage } from "@/lib/siteI18n";
import { readCookie, rootRedirect } from "@/lib/siteLanguages";

// "/" shows the visitor's language (lib/siteLanguages.js): the language they
// chose before (cookie), else their browser's. The Cloudflare Worker
// (lang-worker/) does the same before the page loads, and also knows the
// country; this covers every browser it did not redirect, and the time it is
// off. Robots, the Android app and login links are never redirected, and no
// other address ever is.
function redirectFromRoot() {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return false;
    const { location } = rootRedirect({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      userAgent: navigator.userAgent,
      cookie: readCookie(document.cookie),
      browser: navigator.languages?.length ? navigator.languages : [navigator.language],
    });
    if (!location) return false;
    window.location.replace(location);
    return true;
  } catch {
    return false;
  }
}
const redirecting = redirectFromRoot();

// Marketing CTA clicks (data-track="…") become dataLayer events, only if a
// tag manager is ever installed; nothing is sent anywhere otherwise.
installClickTracking();

// Calls to the billing API carry the session of whoever is signed in.
installApiAuth();

// Disable the right-click context menu site-wide (mild deterrent against casual
// copying — it is NOT a security boundary). Editable fields keep their native
// menu so users can still right-click → paste an IBAN, address, etc. Skipped in
// development so DevTools "Inspect" stays reachable.
if (process.env.NODE_ENV === "production") {
  document.addEventListener("contextmenu", (e) => {
    if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    e.preventDefault();
  });
}

const container = document.getElementById("root");
const prerendered = container.hasAttribute("data-prerendered");
const mount = () => {
  const root = ReactDOM.createRoot(container);
  const app = (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
  // The prerendered page is already on screen, so draw the live one as a
  // transition: React works in small slices and the browser stays
  // responsive (taps, scrolling) while it does.
  if (prerendered) React.startTransition(() => root.render(app));
  else root.render(app);
  // English and Catalan load on demand (lib/i18n.js). Fetch them in the
  // background too, because a document or the help assistant can use a
  // language other than the screen's: at once inside the app, after the
  // page has settled on the public pages.
  if (prerendered) setTimeout(preloadAllLanguages, 4000);
  else preloadAllLanguages();
};

// Wait for the screen language's translations (Spanish ships with the app;
// English and Catalan are a separate file). A public page also arrives with
// its HTML already drawn at build time (scripts/prerender.js): load that
// page's code first, then mount, so React replaces the HTML with the
// identical live page in one go, with no spinner in between. If loading takes
// too long, mount anyway (the spinner shows, exactly as on any other page).
const ready = [i18nReady];
if (prerendered) ready.push(preloadPublicRoute(window.location.pathname));
// A public page in another language (/fr/…) needs that language's texts.
const publicPage = findPage(window.location.pathname);
if (publicPage && !publicPage.app) ready.push(ensureSiteLanguage(pageLanguageAt(window.location.pathname)).catch(() => {}));
const giveUp = new Promise((resolve) => { setTimeout(resolve, 4000); });
// While "/" sends the visitor to their language, nothing is drawn here.
if (!redirecting) Promise.race([Promise.all(ready), giveUp]).then(mount, mount);

// Register the PWA service worker (enables install + Android TWA packaging).
// Auto-update: when a new service worker takes control, reload once so the
// installed app always shows the latest deploy instead of stale cached code.
if ("serviceWorker" in navigator && process.env.NODE_ENV !== "production") {
  // Local dev (npm start): chunk names are NOT hashed (bundle.js,
  // src_pages_TPV_jsx.chunk.js…), so the worker's cache-first /static/ rule
  // serves chunks from an older dev run next to new ones → webpack crashes with
  // "Cannot read properties of undefined (reading 'call')". No worker in dev:
  // remove any left over, drop its cache, and reload once onto fresh code.
  const wasControlled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .then(() => (window.caches ? caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k)))) : null))
    .then(() => { if (wasControlled) window.location.reload(); })
    .catch(() => {});
} else if ("serviceWorker" in navigator) {
  // Was this page load already under a worker's control?
  //
  // On a FIRST visit it is not: the freshly installed worker's clients.claim()
  // still fires controllerchange, and reloading on that made every new visitor
  // (and every incognito/cleared-cache open) load the whole app twice for
  // nothing — the page had just come straight from the network, so the worker
  // taking over has nothing newer to give.
  //
  // Only a controller SWAP — a new build's worker taking over a page that was
  // already controlled — means the code on screen is stale. That is the case
  // the reload exists for, and it still fires.
  const wasControlled = !!navigator.serviceWorker.controller;
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!wasControlled || reloading) return;
    reloading = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      // Check for a new version on load and roughly hourly while the app is open.
      reg.update().catch(() => {});
      setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
      // When a new worker has installed and we already control the page, activate it.
      reg.addEventListener("updatefound", () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            sw.postMessage("SKIP_WAITING");
          }
        });
      });
    }).catch(() => {});
  });
}
